import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
} from '@mui/material';
import {
  AutoAwesome,
  CloseOutlined,
  ContentCopy,
  CheckCircleOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { emailTemplateApi } from '../api/emailCampaignApi';
import apiService from '../../../services/apiService';

interface RecipientContext {
  name?: string;
  company?: string;
  email?: string;
}

interface EmailBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  onUseEmail: (subject: string, content: string) => void;
  recipientContext?: RecipientContext;
}

const quickPrompts = [
  { label: 'Follow-up 跟進', prompt: 'Professional follow-up email after initial meeting' },
  { label: 'Introduction 介紹', prompt: 'Introduction email presenting our services' },
  { label: 'Thank you 感謝', prompt: 'Thank you email for meeting with us' },
  { label: 'Proposal 提案', prompt: 'Email with business proposal summary' },
  { label: 'Demo Request 演示', prompt: 'Request to schedule a product demo' },
  { label: 'Check-in 問候', prompt: 'Friendly check-in email to maintain relationship' },
];

const EmailBuilderDialog: React.FC<EmailBuilderDialogProps> = ({
  open,
  onClose,
  onUseEmail,
  recipientContext,
}) => {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [generatedSubject, setGeneratedSubject] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [copied, setCopied] = useState(false);
  const [showProfileReminder, setShowProfileReminder] = useState(false);
  const emailNavigate = useNavigate();

  // Check company profile completeness when dialog opens
  useEffect(() => {
    if (!open) return;
    const checkProfile = async () => {
      try {
        const resp: any = await apiService.getCompanySettings();
        const data = resp?.data || resp;
        const s = data?.settings;
        if (s && (!s.description || !s.industry || !s.services)) {
          setShowProfileReminder(true);
        } else {
          setShowProfileReminder(false);
        }
      } catch {
        // ignore
      }
    };
    checkProfile();
  }, [open]);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt to generate email 請輸入提示以生成電郵');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGeneratedSubject('');
    setGeneratedContent('');

    try {
      const result = await emailTemplateApi.generateAITemplate(prompt, {
        category: 'FOLLOW_UP',
        recipientName: recipientContext?.name,
        recipientCompany: recipientContext?.company,
        tone: 'professional',
      });

      if (result.subject) {
        setGeneratedSubject(result.subject);
      }
      if (result.htmlContent) {
        // Convert HTML to readable plain text with proper formatting
        const plainText = result.htmlContent
          // Replace <br> and <br/> with newlines
          .replace(/<br\s*\/?>/gi, '\n')
          // Replace closing paragraph tags with double newlines
          .replace(/<\/p>/gi, '\n\n')
          // Replace list items with bullet points
          .replace(/<li>/gi, '\n• ')
          .replace(/<\/li>/gi, '')
          // Replace heading tags with newlines
          .replace(/<\/h[1-6]>/gi, '\n\n')
          // Remove opening tags for lists and other block elements
          .replace(/<(ul|ol|p|div|h[1-6])[^>]*>/gi, '')
          .replace(/<\/(ul|ol|div)>/gi, '\n')
          // Remove remaining HTML tags
          .replace(/<[^>]*>/g, '')
          // Convert HTML entities
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          // Clean up excessive newlines
          .replace(/\n{3,}/g, '\n\n')
          .trim();
        setGeneratedContent(plainText);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate email. Please try again. 生成電郵失敗，請重試。');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseEmail = () => {
    onUseEmail(generatedSubject, generatedContent);
    handleClose();
  };

  const handleClose = () => {
    setPrompt('');
    setGeneratedSubject('');
    setGeneratedContent('');
    setError('');
    setCopied(false);
    onClose();
  };

  const handleCopyContent = () => {
    navigator.clipboard.writeText(`Subject: ${generatedSubject}\n\n${generatedContent}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesome color="primary" />
            <Typography variant="h6">AI Email Builder 電郵生成器</Typography>
          </Stack>
          <IconButton onClick={handleClose} size="small">
            <CloseOutlined />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Recipient Info */}
          {recipientContext?.name && (
            <Alert severity="info" icon={false}>
              <Typography variant="body2">
                <strong>Recipient 收件人:</strong> {recipientContext.name}
                {recipientContext.company && ` from ${recipientContext.company}`}
              </Typography>
            </Alert>
          )}

          {/* Company Profile Reminder */}
          {showProfileReminder && (
            <Alert
              severity="warning"
              onClose={() => setShowProfileReminder(false)}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    handleClose();
                    emailNavigate('/settings/company');
                  }}
                >
                  Go to Settings
                </Button>
              }
            >
              Complete your company profile (description, industry, services) for better AI-generated emails.
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Prompt Input Section */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Describe the email you want to generate 描述您想生成的電郵
            </Typography>
            <TextField
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., Follow-up email after our meeting about the new CRM system, mention the pricing discussion..."
              multiline
              rows={3}
              fullWidth
              disabled={isGenerating}
            />
          </Box>

          {/* Quick Prompts */}
          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
              Quick templates 快速範本:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {quickPrompts.map((qp) => (
                <Chip
                  key={qp.label}
                  label={qp.label}
                  size="small"
                  variant={prompt === qp.prompt ? 'filled' : 'outlined'}
                  color={prompt === qp.prompt ? 'primary' : 'default'}
                  onClick={() => setPrompt(qp.prompt)}
                  disabled={isGenerating}
                />
              ))}
            </Stack>
          </Box>

          {/* Generate Button */}
          <Button
            variant="contained"
            color="primary"
            startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesome />}
            onClick={handleGenerate}
            disabled={isGenerating || !prompt.trim()}
            sx={{ alignSelf: 'flex-start' }}
          >
            {isGenerating ? 'Generating... 生成中...' : 'Generate Email 生成電郵'}
          </Button>

          {/* Generated Content */}
          {(generatedSubject || generatedContent) && (
            <>
              <Divider />
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">
                    Generated Email 生成的電郵
                  </Typography>
                  <Button
                    size="small"
                    startIcon={copied ? <CheckCircleOutline /> : <ContentCopy />}
                    onClick={handleCopyContent}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </Stack>

                {/* Subject */}
                <TextField
                  label="Subject 主旨"
                  value={generatedSubject}
                  onChange={(e) => setGeneratedSubject(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                />

                {/* Content */}
                <TextField
                  label="Content 內容"
                  value={generatedContent}
                  onChange={(e) => setGeneratedContent(e.target.value)}
                  multiline
                  rows={10}
                  fullWidth
                  helperText="You can edit the generated content before using it 您可以在使用前編輯生成的內容"
                />
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel 取消
        </Button>
        <Button
          variant="contained"
          onClick={handleUseEmail}
          disabled={!generatedSubject || !generatedContent}
        >
          Use This Email 使用此電郵
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailBuilderDialog;
